/**
 * FinanceApp - Copyright (c) 2026 Emmanuel TESTE
 * Tous droits réservés - Logiciel propriétaire
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useAI } from "../contexts/AIContext";
import api, { getToken } from "../services/api";

// Un seul moteur : Mistral AI (02/10/2026). Couleur d'accent de l'assistant.
const ACCENT = "#8b5cf6";

const MODULE_ICONS = {
  dashboard:"📊", comptabilite:"📒", tresorerie:"💰", budget:"📊",
  crm:"🤝", stocks:"📦", commissions:"🏆", abonnements:"📦",
  facturation:"🧾", relances:"📬", general:"🤖",
};

function MarkdownText({ text }) {
  const lines = (text || "").split("\n");
  return (
    <div style={{ lineHeight: 1.7, fontSize: 13 }}>
      {lines.map((line, i) => {
        if (line.startsWith("### ")) return <h4 key={i} style={{ margin:"12px 0 4px", color:"#e2e8f0", fontSize:13 }}>{line.slice(4)}</h4>;
        if (line.startsWith("## "))  return <h3 key={i} style={{ margin:"14px 0 6px", color:"#f1f5f9", fontSize:14 }}>{line.slice(3)}</h3>;
        if (line.startsWith("# "))   return <h2 key={i} style={{ margin:"16px 0 8px", color:"#f1f5f9", fontSize:15 }}>{line.slice(2)}</h2>;
        if (line.startsWith("- ") || line.startsWith("* ")) {
          return <div key={i} style={{ display:"flex", gap:8, marginBottom:2 }}><span style={{color:"#3b82f6",flexShrink:0}}>•</span><span>{renderInline(line.slice(2))}</span></div>;
        }
        if (/^\d+\./.test(line)) {
          const [num, ...rest] = line.split(". ");
          return <div key={i} style={{ display:"flex", gap:8, marginBottom:2 }}><span style={{color:"#3b82f6",flexShrink:0,fontWeight:600}}>{num}.</span><span>{renderInline(rest.join(". "))}</span></div>;
        }
        if (line.trim() === "---") return <hr key={i} style={{ border:"none", borderTop:"1px solid #1e293b", margin:"10px 0" }}/>;
        if (!line.trim()) return <div key={i} style={{ height:6 }}/>;
        return <p key={i} style={{ margin:"2px 0" }}>{renderInline(line)}</p>;
      })}
    </div>
  );
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={i} style={{color:"#f1f5f9"}}>{part.slice(2,-2)}</strong>;
    if (part.startsWith("`") && part.endsWith("`"))   return <code key={i} style={{background:"#1e293b",padding:"1px 5px",borderRadius:3,fontSize:12,color:"#38bdf8"}}>{part.slice(1,-1)}</code>;
    return part;
  });
}

export default function AIDrawer({ currentCompanyId }) {
  const { isOpen, module, initialMsg, closeAI } = useAI();
  const [messages,     setMessages]     = useState([]);
  const [input,        setInput]        = useState("");
  const [streaming,    setStreaming]     = useState(false);
  const [quickActions, setQuickActions] = useState([]);
  const [convId,       setConvId]       = useState(null);
  const [streamBuf,    setStreamBuf]    = useState("");
  const bottomRef     = useRef(null);
  const inputRef      = useRef(null);
  const readerRef     = useRef(null);
  const initialMsgRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    api.ai.quickActions(module).then(setQuickActions).catch(() => {});
    inputRef.current?.focus();
  }, [isOpen, module]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamBuf]);

  useEffect(() => {
    if (!isOpen && readerRef.current) {
      try { readerRef.current.cancel(); } catch {}
    }
  }, [isOpen]);

  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setStreamBuf("");

    const userMsg = { role: "user", content: msg };
    const history = [...messages, userMsg];
    setMessages(history);
    setStreaming(true);

    const BASE = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
    try {
      const res = await fetch(`${BASE}/ai/chat`, {
        method: "POST",
        // getToken() d'api.js : la clé « token » lue auparavant n'existait pas → requête sans jeton
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken() || ""}` },
        body: JSON.stringify({
          messages: history, context_module: module,
          company_id: currentCompanyId, conversation_id: convId,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Erreur serveur");
      }

      const reader = res.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buf = "", full = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6).trim();
          if (data === "[DONE]") break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.conv_id) setConvId(parsed.conv_id);
            if (parsed.delta) { full += parsed.delta; setStreamBuf(full); }
            if (parsed.done) { setMessages(prev => [...prev, { role: "assistant", content: full }]); setStreamBuf(""); }
          } catch (e) {
            if (e.message !== "Unexpected token") throw e;
          }
        }
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: "assistant", content: `❌ Erreur : ${err.message}\n\nRéessayez dans quelques instants. Si le problème persiste, contactez le support.` }]);
    } finally {
      setStreaming(false);
    }
  }, [input, messages, module, currentCompanyId, convId, streaming]);

  // Message initial — ref pour éviter la boucle infinie sur send
  useEffect(() => {
    if (isOpen && initialMsg && initialMsg !== initialMsgRef.current) {
      initialMsgRef.current = initialMsg;
      setMessages([]);
      setConvId(null);
      setTimeout(() => send(initialMsg), 0);
    }
    if (!isOpen) { initialMsgRef.current = null; }
  }, [isOpen, initialMsg, send]);

  const reset = () => { setMessages([]); setConvId(null); setStreamBuf(""); };

  if (!isOpen) return null;

  const cur = { color: ACCENT };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:500, display:"flex", alignItems:"stretch", justifyContent:"flex-end" }}>
      <div onClick={closeAI} style={{ position:"absolute", inset:0, background:"rgba(0,0,0,.4)" }}/>
      <div style={{ position:"relative", width:"min(520px, 100vw)", height:"100%", background:"#0d1117", borderLeft:"1px solid #21262d", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"-8px 0 40px rgba(0,0,0,.5)" }}>

        {/* Header */}
        <div style={{ padding:"14px 16px", borderBottom:"1px solid #21262d", display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
          <div style={{ width:32, height:32, borderRadius:"50%", background:"linear-gradient(135deg,#7c3aed,#3b82f6)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>🤖</div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#e2e8f0" }}>Assistant IA <span style={{ fontSize:10, fontWeight:500, color:"#94a3b8" }}>· Mistral AI</span></div>
            <div style={{ fontSize:11, color:"#64748b" }}>
              {MODULE_ICONS[module] || "📊"} Contexte : {module}
              {convId && <span style={{ marginLeft:8, fontSize:10, color:"#334155" }}>#{convId}</span>}
            </div>
          </div>
          <button onClick={reset} style={{ background:"none", border:"1px solid #21262d", borderRadius:6, color:"#64748b", fontSize:11, padding:"3px 8px", cursor:"pointer" }}>+ Nouveau</button>
          <button onClick={closeAI} style={{ background:"none", border:"none", color:"#64748b", fontSize:20, cursor:"pointer", lineHeight:1 }}>×</button>
        </div>

        {/* Messages */}
        <div style={{ flex:1, overflowY:"auto", padding:"16px", display:"flex", flexDirection:"column", gap:12 }}>
          {messages.length === 0 && !streaming && (
            <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16, padding:"20px 0" }}>
              <div style={{ fontSize:40 }}>🤖</div>
              <div style={{ fontSize:15, fontWeight:600, color:"#e2e8f0" }}>Assistant IA {MODULE_ICONS[module]} {module}</div>
              <div style={{ fontSize:12, color:"#64748b", textAlign:"center" }}>Posez votre question ou choisissez une action rapide ci-dessous</div>
              <div style={{ display:"flex", flexDirection:"column", gap:6, width:"100%" }}>
                {quickActions.slice(0,6).map(qa => (
                  <button key={qa.id} onClick={() => send(qa.label)}
                    style={{ background:"#161b22", border:"1px solid #21262d", borderRadius:8, padding:"9px 14px", textAlign:"left", fontSize:12, color:"#94a3b8", cursor:"pointer", fontWeight:500, transition:"border-color .15s, color .15s" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor=cur.color; e.currentTarget.style.color="#e2e8f0"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor="#21262d"; e.currentTarget.style.color="#94a3b8"; }}>
                    {qa.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => {
            const isUser = msg.role === "user";
            return (
              <div key={i} style={{ display:"flex", flexDirection:isUser?"row-reverse":"row", gap:8, alignItems:"flex-start" }}>
                {!isUser && <div style={{ width:28, height:28, borderRadius:"50%", background:"linear-gradient(135deg,#7c3aed,#3b82f6)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>🤖</div>}
                <div style={{ maxWidth:"85%", padding:"10px 14px", borderRadius:isUser?"14px 14px 4px 14px":"14px 14px 14px 4px", background:isUser?cur.color+"22":"#161b22", border:`1px solid ${isUser?cur.color+"44":"#21262d"}`, color:"#e2e8f0" }}>
                  {isUser ? <p style={{ margin:0, fontSize:13 }}>{msg.content}</p> : <MarkdownText text={msg.content} />}
                </div>
              </div>
            );
          })}

          {(streaming || streamBuf) && (
            <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
              <div style={{ width:28, height:28, borderRadius:"50%", background:"linear-gradient(135deg,#7c3aed,#3b82f6)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>🤖</div>
              <div style={{ maxWidth:"85%", padding:"10px 14px", borderRadius:"14px 14px 14px 4px", background:"#161b22", border:"1px solid #21262d", color:"#e2e8f0" }}>
                {streamBuf ? <MarkdownText text={streamBuf} /> : (
                  <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                    {[0,1,2].map(j => <div key={j} style={{ width:6, height:6, borderRadius:"50%", background:cur.color, animation:`pulse 1.2s ${j*0.2}s infinite` }}/>)}
                    <span style={{ fontSize:11, color:"#64748b", marginLeft:6 }}>réflexion…</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{ padding:"12px 16px", borderTop:"1px solid #21262d", flexShrink:0, background:"#0d1117" }}>
          <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
            <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Posez votre question… (Entrée pour envoyer)" rows={2}
              style={{ flex:1, background:"#161b22", border:"1px solid #21262d", borderRadius:10, padding:"10px 12px", color:"#e2e8f0", fontSize:13, resize:"none", outline:"none", fontFamily:"inherit" }}
            />
            <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
              <button onClick={() => send()} disabled={!input.trim() || streaming}
                style={{ width:40, height:40, borderRadius:10, border:"none", cursor:input.trim()&&!streaming?"pointer":"not-allowed", background:input.trim()&&!streaming?"linear-gradient(135deg,#7c3aed,#3b82f6)":"#1e293b", color:"#fff", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center" }}>↑</button>
            </div>
          </div>
          <div style={{ marginTop:6, fontSize:10, color:"#334155", display:"flex", gap:10 }}>
            <span>Entrée : envoyer · Shift+Entrée : saut de ligne</span>
          </div>
        </div>
      </div>
      <style>{`@keyframes pulse{0%,80%,100%{opacity:.2;transform:scale(0.8)}40%{opacity:1;transform:scale(1)}}`}</style>
    </div>
  );
}
